import { AnimatePresence, motion } from 'framer-motion'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { AiFillSafetyCertificate, AiOutlineExclamationCircle, AiTwotonePhone } from 'react-icons/ai'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { getSmsCode, login } from '@/api/login'
import { loginStore, useRecoilState, useRecoilValue, useSetRecoilState } from '@/store'
import { checkOS } from '@/utils/tool'

import { getMobileMaxLength, getMobileValidateStatus, isMobileValid, normalizeMobileInput } from '../../utils'
import Area from '../Area'
import Slider from '../Slider'
const { stepState, stepTxtState, areaListState, mobileState, areaState, needSliderState } = loginStore

export default function Index() {
  const setStep = useSetRecoilState(stepState)
  const [areaCode, setAreaCode] = useRecoilState(areaState)
  const [mobile, setMobile] = useRecoilState(mobileState)
  const [mobileError, setMobileError] = useState('')
  const [input, setInput] = useState('')
  const [codeError, setCodeError] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [countdownText, setCountdownText] = useState(60)
  const needSlider = useRecoilValue(needSliderState) // 是否需要滑块验证
  const countdownRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined)
  const navigate = useNavigate()

  const handleSubmit = useMutation(() => {
    return login({
      areaCode,
      mobile,
      type: 'SMS',
      input,
      // 设备唯一标识 - 指纹
      uniqueDeviceNo: localStorage.visitorId,
      // 1:一键登录 2:验证码登录 3:密码登录 4:微信登录 5：多孩子切换登录
      loginType: 2,
      // 当前用户登录的IP地址
      Ip: '',
      // Web、pc客户端
      deviceId: 'Web',
      // 浏览器名称版本、pc客户端机型：mac、Windows
      model: checkOS(),
      // 版本号
      buildVersion: import.meta.env.VITE_APP_TIME,
      // 6->浏览器、7->pc端
      platform: 6,
      // 老师：0，学生：1
      identity: 1
    })
  })

  useEffect(() => {
    if (handleSubmit.status === 'error') {
      setErrorMsg('登录失败，请重试')
    }
    if (handleSubmit.data) {
      if (handleSubmit.data.status !== 0) {
        setErrorMsg(handleSubmit.data.msg)
      } else {
        if (handleSubmit.status === 'success') {
          const {
            data: { head, username, devices, uid, isVip, token = '', childs = null }
          } = handleSubmit.data
          const { result } = devices
          sessionStorage.upDateSign = 1
          localStorage.deviceTest = result ? 'true' : 'false'
          localStorage.setItem('head', head)
          localStorage.setItem('username', username)
          localStorage.setItem('devices', JSON.stringify(devices))
          localStorage.setItem('userid', uid)
          localStorage.setItem('token', token)
          localStorage.setItem('isVip', isVip ? '1' : '0')
          localStorage.setItem('localMobile', mobile)
          if (childs) {
            const findIdx = childs.findIndex((item) => String(item.id) === uid)
            if (findIdx !== -1) {
              localStorage.setItem('token', childs[findIdx].token)
            }
            // 存储多个账号信息
            localStorage.setItem('__myk_users__', JSON.stringify(childs))
          }
          navigate('/center')
        }
      }
    }
  }, [handleSubmit.status])

  const smsCode = useMutation(({ sessionId, sig, token }: { sessionId: string | undefined; sig: string | undefined; token: string | undefined }) => {
    return getSmsCode({
      areaCode,
      mobile,
      sessionId,
      sign: sig,
      scene: 'nc_login',
      token,
      type: 'LOGIN'
    })
  })

  const countdown = () => {
    countdownRef.current = setInterval(() => {
      setCountdownText((v) => {
        const end = v - 1
        if (end === 0) {
          clearInterval(countdownRef.current)
          return 60
        } else {
          return end
        }
      })
    }, 1000)
  }

  useEffect(() => {
    if (smsCode.status === 'error') {
      setErrorMsg('获取验证码失败，请重试')
      needSlider && window.nc.reset()
    }
    if (smsCode.data && smsCode.data.status !== 0) {
      setErrorMsg(smsCode.data.msg)
      needSlider && window.nc.reset()
    } else {
      if (smsCode.status === 'success') {
        toast.success(smsCode.data.data)
        countdown()
      }
    }
  }, [smsCode.status])

  const changeAreaCode = (nextArea: string) => {
    setAreaCode(nextArea)
    setMobile('')
    setMobileError('')
    setErrorMsg('')
  }

  const inputChange = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const val = e.target.value
    if (type === 'mobile') {
      setMobile(normalizeMobileInput(val, areaCode))
    } else {
      setInput(val)
    }
  }

  useEffect(() => {
    const status = getMobileValidateStatus(mobile, areaCode)
    setMobileError(status)
    setErrorMsg(status === 'error' ? '请输入正确的手机号' : '')
  }, [mobile, areaCode])

  useEffect(() => {
    if (input.length === 6) {
      setCodeError('success')
      setErrorMsg('')
    } else {
      setCodeError('')
      setErrorMsg('')
    }
  }, [input])

  const canGetSms = countdownText === 60 && isMobileValid(mobile, areaCode)

  const ncSign = (data) => {
    if (!isMobileValid(mobile, areaCode)) return
    setErrorMsg('')
    smsCode.mutate(data)
  }

  const resetNc = () => {
    if (!canGetSms) return
    // 不需滑块
    if (!needSlider) {
      smsCode.mutate({
        sessionId: undefined,
        sig: undefined,
        token: undefined
      })
      return
    }
    // 需滑块
    setErrorMsg('请先进行滑动验证')
    window.nc.reset()
  }

  return (
    <Container className="h-full w-full flex flex-col items-start justify-center">
      <div className="text-[24px] text-[#333]">验证码登录</div>
      <div className="w-[350px] my-[30px]">
        <div
          className={`w-full h-[40px] border-b border-[#ddd] flex items-center justify-center mb-[30px] ${
            mobileError === 'success' ? 'border-[#36c236]' : ''
          } ${mobileError === 'error' ? 'border-[red]' : ''}`}
        >
          <div className="w-[40px] h-full flex items-center justify-center">
            <AiTwotonePhone className="text-[28px] text-[#999]" />
          </div>
          <Area onChange={changeAreaCode} areaCode={areaCode} />
          <div className="flex-1 h-full relative">
            <input
              value={mobile}
              onChange={(e) => inputChange(e, 'mobile')}
              className="absolute h-full w-full p-[10px] border-0 outline-none text-[20px] text-[#656565]"
              placeholder="请输入手机号码"
              maxLength={getMobileMaxLength(areaCode)}
            ></input>
          </div>
        </div>
        <div
          className={`w-full h-[40px] border-b border-[#ddd] flex items-center justify-center ${codeError === 'success' ? 'border-[#36c236]' : ''} ${codeError === 'error' ? 'border-[red]' : ''}
        `}
        >
          <div className="w-[40px] h-full flex items-center justify-center">
            <AiFillSafetyCertificate className="text-[28px] text-[#999]" />
          </div>
          <div className="flex-1 h-full relative">
            <input
              value={input}
              onChange={(e) => inputChange(e, 'input')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit.mutate()
                }
              }}
              className="absolute h-full w-full p-[10px] border-0 outline-none text-[20px] text-[#656565]"
              placeholder="请输入验证码"
              maxLength={6}
            ></input>
          </div>
          <div className="w-[120px] h-full flex items-center justify-center">
            <button
              disabled={!canGetSms}
              onClick={resetNc}
              className={`btn-def w-full h-[36px] rounded-full flex items-center justify-center outline-none text-[16px] text-[#FFF]
              ${canGetSms ? 'btn-active' : ''}
            `}
            >
              {countdownText !== 60 ? `重新获取${countdownText}s` : '获取验证码'}
            </button>
          </div>
        </div>
        {needSlider ? (
          <div className={`h-[34px] mt-[30px] ${mobileError === 'success' ? '' : 'hidden'}`}>
            <Slider ncSign={ncSign} />
          </div>
        ) : null}
      </div>
      {errorMsg ? (
        <div className="text-[#e82925] text-[14px] flex items-center mb-[20px]">
          <AiOutlineExclamationCircle className="mr-[5px]" />
          {errorMsg}
        </div>
      ) : null}
      <div className="w-[350px]">
        <div className="w-full mb-[20px] flex items-center justify-between">
          <div className="cursor-pointer text-[14px] text-[#333333]" onClick={() => setStep(1)}>
            密码登录
          </div>
        </div>
        <div className="w-full mb-[20px]">
          <button
            disabled={!(codeError === 'success' && mobileError === 'success')}
            onClick={() => handleSubmit.mutate()}
            className={`btn-def w-[260px] h-[48px] rounded-full flex items-center justify-center outline-none text-[20px] text-[#FFF]
            ${codeError === 'success' && mobileError === 'success' ? 'btn-active' : ''}
            `}
          >
            登录
          </button>
        </div>
        <div className="text-[14px] text-[#333333]">
          没有账号，
          <span className="cursor-pointer text-[#ff783a]" onClick={() => setStep(2)}>
            免费注册
          </span>
        </div>
      </div>
    </Container>
  )
}

const Container = styled(motion.div)`
  .btn-def {
    background: linear-gradient(270deg, #888, #ccc);
  }
  .btn-active {
    background: linear-gradient(90deg, #ff7839, #fe4844);
  }
`
